import { createFitnessId } from '@/lib/fitnessId';
import { retireWorkoutDraftCheckpoint } from '@/lib/fitnessDraftPersistence';
import { workoutDraftRetirementClientId } from '@/lib/fitnessDraftKeys';
import { fitnessWorkoutController, type createFitnessWorkoutController } from '@/lib/fitnessWorkoutController';
import { workoutDraftKey } from '@/lib/fitnessDraft';
import { fitnessDraftWriterState, notifyFitnessDraftWrite } from '@/lib/fitnessDraftWriter';

type Controller = ReturnType<typeof createFitnessWorkoutController>;
export interface WorkoutDiscardReview {
  readonly ownerId: string; readonly epoch: number; readonly lifecycle: number;
  readonly runId: string | null; readonly revision: number | null;
  readonly raw: string | null; readonly writerRevision: number;
}

/** The confirmation names one account, one run and the exact draft it will remove. */
export function reviewWorkoutDiscard(ownerId: string, controller: Controller = fitnessWorkoutController): WorkoutDiscardReview {
  if (!ownerId || controller.getOwner() !== ownerId) throw new Error('fitness_workout_changed');
  const runtime = controller.getOwned(ownerId);
  if (runtime?.busy || runtime?.state.phase === 'complete') throw new Error('fitness_workout_changed');
  const raw = sessionStorage.getItem(workoutDraftKey(ownerId));
  if (!runtime?.state.exercises.length && raw === null) throw new Error('fitness_workout_missing');
  const identity = controller.getIdentity();
  return { ownerId, epoch: identity.epoch, lifecycle: runtime?.lifecycle ?? 0, runId: runtime?.runId ?? null,
    revision: runtime?.revision ?? null, raw, writerRevision: fitnessDraftWriterState(ownerId).revision };
}

export async function discardReviewedWorkout(review: WorkoutDiscardReview, controller: Controller = fitnessWorkoutController): Promise<void> {
  const current = () => {
    const run = controller.getOwned(review.ownerId), identity = controller.getIdentity();
    return controller.getOwner() === review.ownerId && identity.epoch === review.epoch
      && (run?.runId ?? null) === review.runId && (run?.revision ?? null) === review.revision
      && (run?.lifecycle ?? 0) === review.lifecycle && !run?.busy
      && fitnessDraftWriterState(review.ownerId).revision === review.writerRevision;
  };
  const key = workoutDraftKey(review.ownerId);
  if (!current() || sessionStorage.getItem(key) !== review.raw || !current()) throw new Error('fitness_workout_changed');
  const clientId = controller.getOwned(review.ownerId)?.state.clientId ?? (review.raw !== null ? workoutDraftRetirementClientId(review.raw, createFitnessId()) : null);
  if (!clientId) throw new Error('fitness_workout_missing');
  await retireWorkoutDraftCheckpoint(review.ownerId, clientId, current);
  if (!current() || sessionStorage.getItem(key) !== review.raw || !current()) throw new Error('fitness_workout_changed');
  sessionStorage.removeItem(key);
  if (sessionStorage.getItem(key) !== null || !current()) throw new Error('fitness_workout_discard_unconfirmed');
  controller.retire();
  notifyFitnessDraftWrite(review.ownerId);
}
